import React from 'react';
import { availability, AVAILABILITY_AS_OF, Availability, matrixCountries } from '../../data/availability';
import { cca2Of } from '../../data/geography';
import { DataBadge } from '../DataBadge';
import { SourceRef } from '../SourceRef';
import { PageSources } from '../PageSources';
const STYLE: Record<Availability, {
  cls: string;
  short: string;
}> = {
  Available: {
    cls: 'bg-live/15 text-live',
    short: 'Avail.'
  },
  Restricted: {
    cls: 'bg-machine/15 text-machine',
    short: 'Restr.'
  },
  Blocked: {
    cls: 'bg-signal/15 text-signal',
    short: 'Blocked'
  },
  Unknown: {
    cls: 'text-faint',
    short: '?'
  }
};
const FLAG2: Record<string, string> = {
  ...cca2Of,
  ITA: 'it',
  BRA: 'br',
  IDN: 'id',
  RUS: 'ru',
  AUS: 'au'
};
export function AvailabilityMatrix() {
  return <section aria-labelledby="avail-title" className="mt-16" data-id="element-698" data-mp="3aloq63xds7wi">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-699" data-mp="27g5rj50inff">
        <div data-id="element-700" data-mp="6z6i1unifl9h">
          <h2 id="avail-title" className="font-serif text-2xl text-ink md:text-3xl" data-id="element-701" data-mp="6zzpkxuameso">
            Where models are officially available
          </h2>
          <p className="mt-2 max-w-3xl text-[14px] text-muted" data-id="element-702" data-mp="25vh8e8ng9vui">
            Major models and assistants across 15 countries. A cell is marked only where an official list, regulator or reputable report says so. Everything else is “Unknown” rather than assumed.
          </p>
        </div>
      </div>
      <PageSources data-id="element-703" data-mp="2av3hc5w8wpxi">
        <DataBadge mode="snapshot" source="Availability matrix (quarterly)" asOf={AVAILABILITY_AS_OF} data-id="element-704" data-mp="2plkm9z839xz5" />
      </PageSources>

      <div className="mt-4 flex flex-wrap gap-4 text-[12px] text-muted" aria-label="Legend" data-id="element-705" data-mp="2klofjs80bsfw">
        {(['Available', 'Restricted', 'Blocked', 'Unknown'] as Availability[]).map(s => <span key={s} className="inline-flex items-center gap-1.5" data-id="element-706" data-mp="2tn629tfx6sdk">
            <span className={`rounded px-1.5 py-px font-mono text-[10.5px] ${STYLE[s].cls}`} data-id="element-707" data-mp="148hslzyproj8">{STYLE[s].short}</span>
            {s}
          </span>)}
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-line" data-id="element-708" data-mp="zb7snqdp7uh5">
        <table className="w-full min-w-[1180px] border-collapse text-[12.5px]" data-id="element-709" data-mp="2zcxweukcix1w">
          <thead className="bg-surface text-[11.5px] text-muted" data-id="element-710" data-mp="349xtifz7wpgv">
            <tr data-id="element-711" data-mp="399tvyas9o8ub">
              <th scope="col" className="sticky left-0 z-10 border-b border-line bg-surface px-3 py-2.5 text-left font-normal" data-id="element-712" data-mp="399aqhtjszcx6">
                Model · product
              </th>
              {matrixCountries.map(c => <th key={c} scope="col" className="border-b border-line px-1.5 py-2.5 text-center font-normal" data-id="element-713" data-mp="3i9gw6t99f8ls">
                  <span className="flex flex-col items-center gap-1" data-id="element-714" data-mp="3n80m14qz54jf">
                    {FLAG2[c] && <img src={`https://flagcdn.com/${FLAG2[c]}.svg`} alt="" className="h-3 w-4 rounded-[2px] object-cover ring-1 ring-line" data-id="element-715" data-mp="3s1czciq7dowv" />}
                    <span className="font-mono" data-id="element-716" data-mp="vtofhw0ttof">{c}</span>
                  </span>
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-717" data-mp="9vqiy053dmre">
            {availability.map(row => <tr key={row.model} className="border-b border-line/60 last:border-b-0" data-id="element-718" data-mp="1ucpybsx5651z">
                <th scope="row" className="sticky left-0 z-10 whitespace-nowrap bg-bg px-3 py-2 text-left font-normal" data-id="element-719" data-mp="1uad7srdbimdb">
                  <span className="text-[13px] text-ink" data-id="element-720" data-mp="1lbrp8w7uj7cj">{row.model}</span>
                  <span className="block font-mono text-[10.5px] text-faint" data-id="element-721" data-mp="1ldw4ubf3xusn">{row.product}</span>
                </th>
                {matrixCountries.map(c => {
              const cell = row.cells[c];
              const status: Availability = cell?.status ?? 'Unknown';
              return <td key={c} className="px-1 py-2 text-center" title={cell ? `${status}: ${cell.note}` : 'Unknown — no source makes a claim'} data-id="element-722" data-mp="24axazzkee0y6">
                      <span className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 font-mono text-[10.5px] ${STYLE[status].cls}`} data-id="element-723" data-mp="1zb18k4rcmhkq">
                        {STYLE[status].short}
                        <span className="sr-only" data-id="element-724" data-mp="2rzgiwdzjcbpq"> — {status}</span>
                      </span>
                      {cell && (cell.sourceId || cell.href) && <SourceRef id={cell.sourceId} href={cell.href} note={cell.note} figure={`${row.model} in ${c}`} data-id="element-725" data-mp="2bn3bght5sm08" />}
                    </td>;
            })}
              </tr>)}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px] text-muted" data-id="element-726" data-mp="27kf1u7wmfb27">
        EU countries (DEU, FRA, ITA): general-purpose AI obligations under the EU AI Act have applied since Aug 2, 2025, which shapes feature rollouts there.
        <SourceRef id="euAiAct" data-id="element-727" data-mp="2z99stuoh3e34" />
        {' '}Open-weight models can be downloaded wherever the host is reachable, so their rows stay mostly “Unknown” unless a license or regulator restricts them.
      </p>
    </section>;
}